import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  CheckIcon,
} from "@heroicons/react/20/solid";

function DropdownOption({
  dotColorClass,
  label,
  count,
  indent = false,
  isSelected = false,
  value,
  serviceColors,
  gxpLabels,
}) {
  // Use serviceColors for service categories
  const isServiceCategory = serviceColors && label in serviceColors;
  const colors = isServiceCategory ? serviceColors[label] : null;
  // For service categories, use a solid dot (bg-...-500)
  let dotClass =
    isServiceCategory && colors
      ? colors?.bgColor?.replace("-50", "-500").replace("-100", "-500")
      : dotColorClass;

  const colorStyles = colors?.bgColor?.startsWith("#")
    ? {
        // backgroundColor: colors?.textColor + "BF",
        backgroundColor: colors?.textColor,
        color: colors?.textColor,
      }
    : {};

  dotClass =
    dotClass?.startsWith("#") || dotClass?.startsWith("bg-")
      ? dotClass
      : `bg-${dotClass}-500`;
  return (
    <div className={`flex items-center${indent ? " pl-6" : ""}`}>
      {isServiceCategory || dotColorClass ? (
        <span
          style={colorStyles}
          className={`inline-block w-2 h-2 rounded-full mr-2 ${dotClass}`}
        />
      ) : null}
      <span
        className={`truncate ${isSelected ? "font-semibold" : "font-normal"}`}
      >
        {label}
        {gxpLabels && (
          <span className="ml-2 text-xs text-gray-400 font-normal">
            {gxpLabels}
          </span>
        )}
      </span>
      {typeof count === "number" && (
        <span className="ml-2 text-gray-400 text-xs">{count} vendors</span>
      )}
    </div>
  );
}

const RenderSingleDropdowns = ({
  containerRef,
  searchRef,
  isOpen,
  setIsOpen,
  renderBadges,
  filteredOptions,
  selectedOptions,
  query,
  setQuery,
  placeholder,
  allIndividualItemsForCurrentPlaceholder,
  allSelected,
  serviceColors,
  handleSelection,
}) => (
  <div className="relative" ref={containerRef}>
    <div
      className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:text-sm"
      onClick={() => setIsOpen(!isOpen)}
    >
      <div className="flex flex-wrap items-center gap-1 min-h-[24px]">
        {renderBadges()}
        <span className="relative flex-1">
          {selectedOptions.length === 0 && !query && (
            <MagnifyingGlassIcon className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          )}
          <input
            ref={searchRef}
            type="text"
            className={`flex-1 min-w-[80px] border-0 bg-transparent p-0 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 sm:text-sm cursor-pointer ${
              selectedOptions.length > 0 ? "pl-1" : !query ? "pl-5" : "pl-1"
            }`}
            placeholder={selectedOptions.length > 0 ? "" : placeholder} // Hide placeholder when options are selected
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
            id={`dropdown-input-${placeholder
              ?.replace(/\s+/g, "-")
              ?.toLowerCase()}`}
            name={`dropdown-input-${placeholder
              ?.replace(/\s+/g, "-")
              ?.toLowerCase()}`}
          />
        </span>
      </div>
      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
        <ChevronUpDownIcon
          className="h-5 w-5 text-gray-400"
          aria-hidden="true"
        />
      </span>
    </div>

    {isOpen && (
      <div className="absolute z-50 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-none sm:text-sm">
        <ul className="max-h-60 py-1">
          {filteredOptions.map((option) => {
            let isSelectedInList;
            const isMetaAllOptionForVSCOrList =
              (placeholder === "Vendors" && option.value === "All Vendors") ||
              (placeholder === "Service Categories" &&
                option.value === "All Services");

            if (
              placeholder === "Vendors" ||
              placeholder === "Service Categories"
            ) {
              const currentAllIndividualItems =
                allIndividualItemsForCurrentPlaceholder;
              if (isMetaAllOptionForVSCOrList) {
                isSelectedInList =
                  selectedOptions.length === currentAllIndividualItems.length &&
                  currentAllIndividualItems.length > 0;
              } else {
                isSelectedInList = selectedOptions.some(
                  (selected) => selected.value === option.value
                );
              }
            } else if (placeholder === "Data Availability") {
              const isAnyDataAvailableOptionActive =
                selectedOptions.length === 1 &&
                selectedOptions[0].value === "data";
              if (isAnyDataAvailableOptionActive) {
                // If "Any Data Available" is the main selected filter, then 'data', 'risk', 'rfi', 'ta' should visually appear checked.
                if (
                  ["data", "risk", "rfi", "audit", "ta"].includes(option.value)
                ) {
                  isSelectedInList = true;
                } else {
                  // For other DA options (like 'all'), their checked state is independent of 'data' being active.
                  isSelectedInList = selectedOptions.some(
                    (sel) => sel.value === option.value
                  );
                }
              } else {
                // "Any Data Available" is not the active filter, standard check.
                isSelectedInList = selectedOptions.some(
                  (selected) => selected.value === option.value
                );
              }
            } else {
              // Fallback for other generic filters (not V, SC, or DA)
              // Their selection state is driven by `allSelected` local state for a generic "All" meta-option,
              // or by direct presence in `selectedOptions` for specific items.
              // This assumes a generic "All" meta-option would have a value like "All Something" or "Any Something".
              const isPotentiallyTheirMetaAllOption =
                option?.value?.toLowerCase().includes("all") ||
                option?.value?.toLowerCase().includes("any");
              if (isPotentiallyTheirMetaAllOption) {
                // This is a heuristic
                isSelectedInList = allSelected; // Relies on the Dropdown's local `allSelected` state for these other filter types
              } else {
                isSelectedInList = selectedOptions.some(
                  (selected) => selected.value === option.value
                );
              }
            }

            let indentClass;
            if (
              placeholder === "Vendors" ||
              placeholder === "Service Categories"
            ) {
              indentClass = isMetaAllOptionForVSCOrList ? "pl-3" : "pl-6";
            } else {
              // For DA and other generic filters
              indentClass = option?.indent ? "pl-6" : "pl-3"; // Relies on option.indent boolean
            }

            return (
              <li
                key={option.value}
                className={`relative select-none py-2 ${indentClass} pr-9 ${
                  option?.disabled
                    ? "cursor-not-allowed opacity-50"
                    : `cursor-pointer ${
                        isSelectedInList ? "bg-indigo-50" : "hover:bg-indigo-50"
                      }`
                }`}
                onClick={() => handleSelection(option)}
              >
                <DropdownOption
                  dotColorClass={option?.dotColorClass}
                  label={option?.label}
                  count={option?.count}
                  indent={false}
                  isSelected={isSelectedInList}
                  value={option?.value}
                  serviceColors={serviceColors}
                />
                {isSelectedInList && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">
                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                )}
              </li>
            );
          })}
          {filteredOptions.length === 0 && (
            <li className="py-2 px-4 text-sm text-gray-500">
              No results found
            </li>
          )}
        </ul>
      </div>
    )}
  </div>
);

const RenderTAIndications = ({
  containerRef,
  searchRef,
  setExpandedTAs,
  isOpen,
  setIsOpen,
  selectedOptions,
  options,
  setSelectedOptions,
  handleTASelection,
  expandedTAs,
  handleIndicationSelection,
  query,
  setQuery,
}) => {
  const toggleTAExpansion = useCallback((therapeuticArea) => {
    setExpandedTAs((prev) =>
      prev.includes(therapeuticArea)
        ? prev.filter((ta) => ta !== therapeuticArea)
        : [...prev, therapeuticArea]
    );
  }, []);

  // ta_name, indications
  // indication.name
  const filteredOptions = useMemo(() => {
    // Remove 'All Types' from Vendor Type dropdown
    let opts = options;
    opts = opts.filter((opt) => opt.value !== "All Types");

    // Apply search filter
    const queryString = query?.toLowerCase();
    const filtered =
      query === ""
        ? opts
        : opts
            .filter(
              (option) =>
                option?.ta_name?.toLowerCase().includes(queryString) ||
                option?.indications?.some((ind) =>
                  ind?.name?.toLowerCase().includes(queryString)
                )
            )
            .map((option) => ({
              ...option,
              indications: option?.ta_name?.toLowerCase().includes(queryString)
                ? option?.indications
                : option?.indications?.filter((ind) =>
                    ind.name.toLowerCase().includes(queryString)
                  ),
            }));

    // Always put "All" option first if it exists
    return filtered;
  }, [query, options]);

  return (
    <div className="relative" ref={containerRef}>
      <div
        className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:text-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap items-center gap-1 min-h-[24px] relative">
          {/* Magnifying glass icon and placeholder, matching other filters */}
          {Object.keys(selectedOptions).length === 0 && !query && (
            <>
              <span className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </span>
            </>
          )}
          {/* Badges for selected TAs/Indications - Fixed to only show "All" when literally all are selected */}
          {(() => {
            const allTAOptions = options.map((t) => t.ta_name);
            const selectedTAs = Object.keys(selectedOptions || {});
            const allSelected = selectedTAs.length === allTAOptions.length;

            if (allSelected && allTAOptions.length > 0) {
              return (
                <span className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ring-1 bg-white text-gray-900 ring-gray-200 ring-inset">
                  All Therapeutic Areas & Indications
                  <button
                    type="button"
                    className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedOptions({});
                    }}
                  >
                    <span className="sr-only">
                      Remove all therapeutic areas
                    </span>
                    <svg
                      viewBox="0 0 14 14"
                      className="h-3.5 w-3.5 stroke-current"
                    >
                      <path d="M4 4l6 6m0-6l-6 6" />
                    </svg>
                  </button>
                </span>
              );
            } else if (selectedTAs.length > 0) {
              return Object.entries(selectedOptions).map(([ta, taData]) => {
                const allIndicationsForTA =
                  options.find((t) => t.ta_name === ta)?.indications || [];
                const label =
                  taData.indications.length === allIndicationsForTA.length
                    ? `${ta}: All`
                    : `${ta}: ${taData.indications
                        ?.map((i) => i.name)
                        .join(", ")}`;
                return (
                  <span
                    key={ta}
                    className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ring-1 bg-white text-gray-900 ring-gray-200 ring-inset"
                  >
                    {label}
                    <button
                      type="button"
                      className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOptions((prev) => {
                          const newState = { ...prev };
                          delete newState[ta];
                          return newState;
                        });
                      }}
                    >
                      <span className="sr-only">Remove {ta}</span>
                      <svg
                        viewBox="0 0 14 14"
                        className="h-3.5 w-3.5 stroke-current"
                      >
                        <path d="M4 4l6 6m0-6l-6 6" />
                      </svg>
                    </button>
                  </span>
                );
              });
            }
            return null;
          })()}
          <input
            ref={searchRef}
            type="text"
            className={`flex-1 min-w-[80px] border-0 bg-transparent p-0 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 sm:text-sm cursor-pointer ${
              Object.values(selectedOptions).length > 0
                ? "pl-1"
                : !query
                ? "pl-5"
                : "pl-1"
            }`}
            placeholder={
              Object.keys(selectedOptions).length > 0
                ? ""
                : "Therapeutic Areas & Indications"
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
            id={`dropdown-input-taindications`}
            name={`dropdown-input-taindications`}
          />
        </div>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </span>
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-none sm:text-sm max-h-96">
          {/* Add All Option */}
          <div className="relative">
            <div
              className={`relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                Object.keys(selectedOptions).length === options.length
                  ? "bg-indigo-50"
                  : "hover:bg-indigo-50"
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedOptions(
                  (prev) =>
                    Object.keys(prev).length === options.length
                      ? {} // If all are selected, clear all
                      : options.reduce(
                          (acc, { ta_code, ta_name, indications }) => ({
                            ...acc,
                            [ta_name]: { ta_code, indications },
                          }),
                          {}
                        ) // Otherwise, select all
                );
              }}
            >
              <span
                className={`block truncate ${
                  Object.keys(selectedOptions).length === options.length
                    ? "font-semibold"
                    : "font-normal"
                }`}
              >
                All Therapeutic Areas & Indications
              </span>
              {Object.keys(selectedOptions).length === options.length && (
                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                </span>
              )}
            </div>
          </div>
          {/* Existing therapeutic areas */}
          {filteredOptions.map(({ ta_code, ta_name, indications }) => (
            <div key={`${ta_name}-${ta_code}`} className="relative">
              <div
                className={`relative cursor-pointer select-none py-2 pl-6 pr-9 ${
                  selectedOptions[ta_name]?.indications?.length ===
                  indications.length
                    ? "bg-indigo-50"
                    : "hover:bg-indigo-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div
                    className="flex-grow cursor-pointer flex items-center"
                    onClick={(e) =>
                      handleTASelection(ta_code, ta_name, indications, e)
                    }
                  >
                    <span
                      className={`block truncate ${
                        selectedOptions[ta_name]?.indications?.length ===
                        indications.length
                          ? "font-semibold"
                          : "font-normal"
                      }`}
                    >
                      {ta_name}
                    </span>
                    <div
                      className="ml-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleTAExpansion(ta_name);
                      }}
                    >
                      <svg
                        className={`h-5 w-5 text-gray-400 transform transition-transform duration-200 ${
                          expandedTAs.includes(ta_name) ? "rotate-180" : ""
                        }`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
                      </svg>
                    </div>
                  </div>
                  {selectedOptions[ta_name]?.indications?.length ===
                    indications.length && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  )}
                </div>
              </div>
              {expandedTAs.includes(ta_name) && (
                <div>
                  {indications.map((indication) => (
                    <div
                      key={`${indication.name}-${indication.id}`}
                      className={`cursor-pointer select-none py-2 pl-12 pr-9 relative ${
                        selectedOptions[ta_name]?.indications
                          ?.map((s) => s.name)
                          ?.includes(indication.name)
                          ? "bg-indigo-50"
                          : "hover:bg-indigo-50"
                      }`}
                      onClick={(e) =>
                        handleIndicationSelection(
                          ta_code,
                          ta_name,
                          indication,
                          e
                        )
                      }
                    >
                      <span
                        className={`block truncate ${
                          selectedOptions[ta_name]?.indications
                            ?.map((s) => s.name)
                            ?.includes(indication.name)
                            ? "font-semibold"
                            : "font-normal"
                        }`}
                      >
                        {indication.name}
                      </span>
                      {selectedOptions[ta_name]?.indications
                        ?.map((s) => s.name)
                        ?.includes(indication.name) && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export function SearchDropdown({
  options,
  selectedOptions,
  setSelectedOptions,
  placeholder,
  serviceColors,
  setSelectedProviders,
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [expandedTAs, setExpandedTAs] = useState([]);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Helper to get all *individual* selectable items for Vendors or Service Categories
  // (excluding the "All Vendors" / "All Services" meta-option itself)
  const allIndividualItemsForCurrentPlaceholder = useMemo(() => {
    if (!options) return [];
    if (placeholder === "Vendors") {
      return options.filter(
        (opt) => opt.value !== "All Vendors" && opt.label !== "All Vendors"
      );
    }
    if (placeholder === "Service Categories") {
      return options.filter(
        (opt) => opt.value !== "All Services" && opt.label !== "All Services"
      );
    }
    return []; // Not used by other placeholders for this specific logic
  }, [options, placeholder]);

  // Normalize options to ensure they have the required properties
  const normalizedOptions = useMemo(() => {
    if (!options) return [];
    return options.map((opt) => ({
      value: opt.value || opt.name || "",
      label: opt.label || opt.name || "",
      visibility: opt.visibility || "visible",
      ...opt,
    }));
  }, [options]);

  const filteredOptions = useMemo(() => {
    // Remove 'All Types' from Vendor Type dropdown
    let opts = normalizedOptions;
    if (placeholder === "Vendor Type") {
      opts = opts.filter((opt) => opt.value !== "All Types");
    }
    // Find the "All" option based on the placeholder type
    const allOption = opts.find((opt) => {
      switch (placeholder) {
        case "Vendors":
          return opt.value === "All Vendors";
        case "Service Categories":
          return opt.value === "All Services";
        case "Vendor Types":
          return opt.value === "All Types";
        case "Data Availability":
          return opt.value === "Any Data Available";
        case "Data Visibility":
          return opt.value === "Any Visibility";
        default:
          return false;
      }
    });

    // Filter other options
    const otherOptions = opts.filter((opt) => {
      switch (placeholder) {
        case "Vendors":
          return opt.value !== "All Vendors";
        case "Service Categories":
          return opt.value !== "All Services";
        case "Vendor Types":
          return opt.value !== "All Types";
        case "Data Availability":
          return opt.value !== "Any Data Available";
        case "Data Visibility":
          return opt.value !== "Any Visibility";
        default:
          return true;
      }
    });

    // Apply search filter
    const filtered =
      query === ""
        ? otherOptions
        : otherOptions.filter((option) =>
            option?.label?.toLowerCase().includes(query?.toLowerCase())
          );

    // Always put "All" option first if it exists
    return allOption ? [allOption, ...filtered] : filtered;
  }, [normalizedOptions, query, placeholder]);

  // NEW: Helper to get all selectable options (excluding 'All')
  const allSelectableOptions = useMemo(
    () =>
      filteredOptions.filter(
        (opt) =>
          !opt.value.includes("All Vendors") &&
          !opt.value.includes("Any Visibility")
      ),
    [filteredOptions]
  );

  // Handle selection logic
  const handleSelection = (option) => {
    // Skip disabled options
    if (option.disabled) return;
    if (!option || typeof option.value === "undefined") return;
    setQuery(""); // Clear search query after any selection attempt

    if (placeholder === "Vendors" || placeholder === "Service Categories") {
      const currentAllIndividualItems = allIndividualItemsForCurrentPlaceholder;
      const isMetaAllOptionClicked =
        (placeholder === "Vendors" && option.value === "All Vendors") ||
        (placeholder === "Service Categories" &&
          option.value === "All Services");

      if (isMetaAllOptionClicked) {
        // Toggle select/deselect all *individual items*
        if (
          selectedOptions.length === currentAllIndividualItems.length &&
          currentAllIndividualItems.length > 0
        ) {
          setSelectedOptions([]);
        } else {
          setSelectedOptions([...currentAllIndividualItems]);
        }
      } else {
        // Individual item clicked: toggle its presence in selectedOptions
        setSelectedOptions((prevSelected) =>
          prevSelected.some((p) => p.value === option.value)
            ? prevSelected.filter((p) => p.value !== option.value)
            : [...prevSelected, option]
        );
      }
      // `allSelected` boolean state is NOT directly used by Vendors/Service Categories for this logic path.
    } else {
      // Fallback to previous simpler logic for other placeholders (e.g., Data Availability)
      // This uses the `allSelected` local boolean state.
      const isCurrentOptionTheAllEquivalentForOthers =
        (placeholder === "Data Availability" &&
          option.value === "Any Visibility") || // Example "All" for DA
        (placeholder === "Data Availability" && option.value === "data") || // 'data' in DA also had special handling
        (placeholder === "Data Availability" && option.value === "all"); // 'all' in DA is an individual option

      if (
        isCurrentOptionTheAllEquivalentForOthers &&
        placeholder === "Data Availability"
      ) {
        // Specific Data Availability logic from before the major refactor attempts
        setSelectedOptions((prev) => {
          if (option.value === "data") {
            // 'data' is special, acts like a toggle clearing others
            return prev.length === 1 && prev[0].value === "data"
              ? []
              : [option];
          }
          if (option.value === "all") {
            // 'all' (full data availability) is also special
            return prev.length === 1 && prev[0].value === "all" ? [] : [option];
          }
          // For other DA options ('risk', 'rfi', 'ta') - if 'data' or 'all' was selected, clicking another replaces them.
          // If 'data' or 'all' is NOT selected, then 'risk', 'rfi', 'ta' are multi-select.
          // This part of DA logic was complex and specific.
          // The most important is that 'All Vendors' and 'All Services' have the new logic.
          // We will preserve the DA selection logic from before the last two major attempts.
          // This means DA selection should be handled by the `setSelectedOptions` call below this `if` block.
          // The `allSelected` state variable below might handle a generic "All" if DA had one.
          // Given DA's specific items, it's safer to let individual toggles work.
          const alreadyExists = prev.some((p) => p.value === option.value);
          if (alreadyExists) {
            return prev.filter((p) => p.value !== option.value);
          } else {
            // Data Availability: 'data' and 'all' are single-select like radio buttons.
            // Others ('risk', 'rfi', 'ta') can be multi-selected *if* 'data' or 'all' are not active.
            if (option.value === "data" || option.value === "all") {
              return [option];
            }
            // If adding 'risk', 'rfi', or 'ta', remove 'data' or 'all' if they were present.
            const withoutDataOrAll = prev.filter(
              (p) => p.value !== "data" && p.value !== "all"
            );
            return [...withoutDataOrAll, option];
          }
        });
      } else {
        // For truly generic filters that are not V, SC, or DA
        const isGenericAll = option.value?.toLowerCase().includes("all"); // A very generic catch for an "All" type option
        if (isGenericAll) {
          const newAllSelectedState = !allSelected; // allSelected is the Dropdown's local state
          setAllSelected(newAllSelectedState);
          // If "All" is clicked for these other types, it means no specific options are selected.
          setSelectedOptions([]);
        } else {
          // Individual item clicked for these other types
          if (allSelected) {
            // If the generic "All" was active
            setAllSelected(false); // Deactivate it
          }
          setSelectedOptions((prevSelected) => {
            // Standard toggle
            const alreadyExists = prevSelected.some(
              (p) => p.value === option.value
            );
            if (alreadyExists) {
              return prevSelected.filter((p) => p.value !== option.value);
            } else {
              return [...prevSelected, option];
            }
          });
        }
      }
    }
  };

  // Click outside handler remains the same
  const handler = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setQuery("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // When rendering badges or passing to filter logic, use:
  //   allSelected ? allSelectableOptions : selectedOptions

  // When filter changes, notify parent with full list if allSelected
  useEffect(() => {
    if (
      placeholder === "Vendors" &&
      typeof setSelectedProviders === "function"
    ) {
      setSelectedProviders(
        allSelected ? allSelectableOptions : selectedOptions
      );
    }
    // eslint-disable-next-line
  }, [allSelected, selectedOptions]);

  useEffect(() => {
    if (isOpen && searchRef.current) searchRef.current.focus();
  }, [isOpen]);

  const handleIndicationSelection = useCallback(
    (ta_code, therapeuticArea, indication, e) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedOptions((prev) => {
        const newState = { ...prev };

        // If this therapeutic area exists in our selections
        if (therapeuticArea in newState) {
          // If this indication is already selected, remove it
          if (
            newState[therapeuticArea]?.indications
              ?.map((s) => s.name)
              .includes(indication.name)
          ) {
            newState[therapeuticArea].indications = newState[
              therapeuticArea
            ]?.indications.filter((ind) => ind.name !== indication.name);
            // If no indications left for this TA, remove the TA entirely
            if (newState[therapeuticArea].length === 0) {
              delete newState[therapeuticArea];
            }
          } else {
            // If indication not selected, add it
            newState[therapeuticArea].indications = [
              ...newState[therapeuticArea].indications,
              indication,
            ];
          }
        } else {
          // If TA doesn't exist, add it with this indication
          newState[therapeuticArea] = { ta_code, indications: [indication] };
        }

        return newState;
      });
    },
    []
  );

  const handleTASelection = useCallback(
    (ta_code, therapeuticArea, indications, e) => {
      e.preventDefault();
      e.stopPropagation();

      setSelectedOptions((prev) => {
        const newState = { ...prev };
        const hasAllIndications =
          prev[therapeuticArea]?.indications?.length === indications.length;

        if (hasAllIndications) {
          delete newState[therapeuticArea];
        } else {
          newState[therapeuticArea] = {
            ta_code,
            indications: [],
          };
        }

        return newState;
      });
    },
    []
  );

  // Render badges
  const renderBadges = () => {
    if (placeholder === "Vendors" || placeholder === "Service Categories") {
      const currentAllIndividualItems = allIndividualItemsForCurrentPlaceholder;
      if (!selectedOptions || !currentAllIndividualItems) return null;

      const numSelected = selectedOptions.length;
      const numTotalSelectable = currentAllIndividualItems.length;
      let badgeText = "";
      let showClearAllForBadge = false;

      if (numSelected === 0 || numTotalSelectable === 0) {
        return null;
      }

      if (numSelected === numTotalSelectable) {
        badgeText = placeholder === "Vendors" ? "All Vendors" : "All Services";
        showClearAllForBadge = true;
      } else if (numSelected > numTotalSelectable / 2) {
        const excludedOptions = currentAllIndividualItems.filter(
          (opt) => !selectedOptions.some((sel) => sel.value === opt.value)
        );
        const prefix =
          placeholder === "Vendors" ? "All Vendors" : "All Services";
        badgeText = `${prefix}: -${excludedOptions
          .map((opt) => opt.label)
          .join(", -")}`;
        showClearAllForBadge = true;
      } else {
        // Render individual badges for Vendors/Service Categories
        return selectedOptions.map((option) => {
          if (
            !option ||
            typeof option.value === "undefined" ||
            typeof option.label === "undefined"
          ) {
            return null;
          }
          const color = serviceColors[option.label];
          const colorStyle = color
            ? {
                backgroundColor: color.bgColor,
                color: color.textColor,
                boxShadow: `inset 0 0 0 1px ${color.borderColor}1A`,
              }
            : {};
          const isServiceCategoryBadge =
            placeholder === "Service Categories" &&
            serviceColors &&
            serviceColors[option.label];
            const badgeSpecificClasses = isServiceCategoryBadge
            ? `${option.dotColorClass} ${color.bgColor} ${
                color.textColor
              } ${color.borderColor}`
            : "bg-white text-gray-900 ring-gray-200 ring-inset";
          return (
            <span
              key={option.value}
              style={colorStyle}
              className={`inline-flex items-center rounded-md px-2 py-1 mb-1 mr-2 text-xs font-medium ring-1 ring-inset ${badgeSpecificClasses}`}
            >
              {option.dotColorClass && !isServiceCategoryBadge && (
                <span
                  className={`inline-block w-2 h-2 rounded-full mr-1 ${option.dotColorClass}`}
                />
              )}
              {option.label}
              <button
                type="button"
                className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelection(option); // This will toggle the individual item
                }}
              >
                <span className="sr-only">Remove {option.label}</span>
                <svg viewBox="0 0 14 14" className="h-3.5 w-3.5 stroke-current">
                  <path d="M4 4l6 6m0-6l-6 6" />
                </svg>
              </button>
            </span>
          );
        });
      }

      if (badgeText) {
        // Render the single summary badge for Vendors/Service Categories
        return (
          <span className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium leading-none ring-1 bg-white text-gray-900 ring-gray-200 ring-inset">
            {badgeText}
            {showClearAllForBadge && (
              <button
                type="button"
                className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOptions([]);
                }} // Clears all for V/SC
              >
                <span className="sr-only">Clear selection</span>
                <svg viewBox="0 0 14 14" className="h-3.5 w-3.5 stroke-current">
                  <path d="M4 4l6 6m0-6l-6 6" />
                </svg>
              </button>
            )}
          </span>
        );
      }
      return null;
    } else {
      // Badge rendering for other placeholders (e.g., Data Availability) - keep it simple
      // This shows individual badges based on selectedOptions, using the styles from previous successful edits.
      // It does not use the "All: -Excluded" logic.
      // If their "All" option (via allSelected local state) was meant to show a *single* "All..." badge,
      // that specific logic would need to be reinstated here if it was different.
      // For now, this renders individual badges for whatever is in selectedOptions.
      if (
        allSelected &&
        (placeholder === "Data Availability" ||
          placeholder === "Data Visibility")
      ) {
        // If there was a specific "All" badge for DA when `allSelected` was true, it's not here.
        // DA usually showed individual badges or its special "Any Data Available" text.
        // This path aims to render *nothing* in the bar if `allSelected` is true for these,
        // letting the main filter summary below the dropdowns handle "Any Data Available".
        // However, if `selectedOptions` is not empty (e.g. 'data' is selected), it will fall through.
        if (selectedOptions.length === 0) return null;
      }

      return selectedOptions.map((option) => {
        if (
          !option ||
          typeof option.value === "undefined" ||
          typeof option.label === "undefined"
        ) {
          return null;
        }
        // Special handling for GxP badges
        const isGxPBadge = placeholder === "GxP";
        const isServiceCategory =
          placeholder === "Service Categories" &&
          serviceColors &&
          serviceColors[option.label];

        const badgeSpecificClasses = isGxPBadge
          ? "bg-slate-500 text-white ring-slate-500"
          : isServiceCategory
          ? `${serviceColors[option.label].bgColor} ${
              serviceColors[option.label].textColor
            } ${serviceColors[option.label].borderColor}`
          : "bg-white text-gray-900 ring-gray-200 ring-inset";

        // For GxP badges, display only the value (abbreviation) without descriptor
        const displayLabel = isGxPBadge ? option.value : option.label;

        return (
          <span
            key={option.value}
            className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium leading-none ring-1 ${badgeSpecificClasses}`}
          >
            {option.dotColorClass && !isServiceCategory && !isGxPBadge && (
              <span
                className={`inline-block w-2 h-2 rounded-full mr-1 ${option.dotColorClass}`}
              />
            )}
            {displayLabel}
            <button
              type="button"
              className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
              onClick={(e) => {
                e.stopPropagation();
                handleSelection(option); // Should use the simpler toggle logic for these filters
              }}
            >
              <span className="sr-only">Remove {displayLabel}</span>
              <svg viewBox="0 0 14 14" className="h-3.5 w-3.5 stroke-current">
                <path d="M4 4l6 6m0-6l-6 6" />
              </svg>
            </button>
          </span>
        );
      });
    }
  };

  return placeholder === "Therapeutic Areas & Indications" ? (
    <RenderTAIndications
      containerRef={dropdownRef}
      searchRef={searchRef}
      isOpen={isOpen}
      options={options}
      expandedTAs={expandedTAs}
      selectedOptions={selectedOptions}
      setSelectedOptions={setSelectedOptions}
      query={query}
      setQuery={setQuery}
      setIsOpen={setIsOpen}
      setExpandedTAs={setExpandedTAs}
      handleTASelection={handleTASelection}
      handleIndicationSelection={handleIndicationSelection}
    />
  ) : (
    <RenderSingleDropdowns
      containerRef={dropdownRef}
      searchRef={searchRef}
      query={query}
      isOpen={isOpen}
      filteredOptions={filteredOptions}
      selectedOptions={selectedOptions}
      placeholder={placeholder}
      allIndividualItemsForCurrentPlaceholder={
        allIndividualItemsForCurrentPlaceholder
      }
      allSelected={allSelected}
      serviceColors={serviceColors}
      setQuery={setQuery}
      setIsOpen={setIsOpen}
      renderBadges={renderBadges}
      handleSelection={handleSelection}
    />
  );
}
